import { ModelStatus, ValuationStatus } from './types';

// Export enum values as constants for backward compatibility
const { DRAFT } = ModelStatus;
const { REVIEW } = ModelStatus;
const { IN_PROCESS_SCALAR_USA } = ModelStatus;
const { FINAL } = ModelStatus;
const { REVIEW_FINAL } = ModelStatus;
const { COMPLETE } = ModelStatus;

const { READY_FOR_ONBOARDING } = ValuationStatus;
const { ONBOARDING_SENT } = ValuationStatus;
const { ONBOARDING_FINISHED } = ValuationStatus;
const { CLIENT_DOCS_RECEIVED } = ValuationStatus;
const { CLIENT_DOCS_RECEIVED_SKIP_SUPPORT } = ValuationStatus;
const { CLIENT_DOCS_RECEIVED_ASSIGN_INTERN } = ValuationStatus;
const { MODEL_DRAFTED } = ValuationStatus;
const { DRAFT_PREPARED } = ValuationStatus;
const { DRAFT_REVIEWED_AND_SENT } = ValuationStatus;
const { CLIENT_APPROVAL_RECEIVED } = ValuationStatus;
const { REPORT_IN_PROCESS } = ValuationStatus;
const { FINAL_REPORT_UPLOADED } = ValuationStatus;
const { FINAL_SENT } = ValuationStatus;
const { WAITING } = ValuationStatus;
const { AWAITING_FINAL_PAYMENT } = ValuationStatus;
const { CANCELLED } = ValuationStatus;

// Export arrays from the enum

export const STATUS_COLORS = {
  // Model Status Steps
  [ModelStatus.DRAFT]: '#E3F2FD',
  [ModelStatus.REVIEW]: '#FFF3E0',
  [ModelStatus.IN_PROCESS_SCALAR_USA]: '#F3E5F5',
  [ModelStatus.FINAL]: '#E8F5E8',
  [ModelStatus.REVIEW_FINAL]: '#E8F5E8',
  [ModelStatus.COMPLETE]: '#C8E6C9',
  [ModelStatus.WAITING]: '#FFF9C4',

  // Process Status Steps
  [ValuationStatus.READY_FOR_ONBOARDING]: '#E1F5FE',
  [ValuationStatus.ONBOARDING_SENT]: '#E0F2F1',
  [ValuationStatus.ONBOARDING_FINISHED]: '#F1F8E9',
  [ValuationStatus.CLIENT_DOCS_RECEIVED]: '#F9FBE7',
  [ValuationStatus.CLIENT_DOCS_RECEIVED_SKIP_SUPPORT]: '#FFFDE7',
  [ValuationStatus.CLIENT_DOCS_RECEIVED_ASSIGN_INTERN]: '#FFFDE7',
  [ValuationStatus.MODEL_DRAFTED]: '#FFF8E1',
  [ValuationStatus.DRAFT_PREPARED]: '#FFECB3',
  [ValuationStatus.DRAFT_REVIEWED_AND_SENT]: '#FFE0B2',
  [ValuationStatus.CLIENT_APPROVAL_RECEIVED]: '#DCEDC8',
  [ValuationStatus.REPORT_IN_PROCESS]: '#C5E1A5',
  [ValuationStatus.FINAL_REPORT_UPLOADED]: '#A5D6A7',
  [ValuationStatus.FINAL_SENT]: '#81C784',
  [ValuationStatus.AWAITING_FINAL_PAYMENT]: '#BCAAA4',
  [ValuationStatus.CANCELLED]: '#FFCDD2',
} as const;

export const DEFAULT_JOBS_COLOR_PALETTE = {
  READY_FOR_ONBOARDING: STATUS_COLORS[ValuationStatus.READY_FOR_ONBOARDING],
  ONBOARDING_SENT: STATUS_COLORS[ValuationStatus.ONBOARDING_SENT],
  ONBOARDING_FINISHED: STATUS_COLORS[ValuationStatus.ONBOARDING_FINISHED],
  CLIENT_DOCS_RECEIVED: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED],
  CLIENT_DOCS_RECEIVED_SKIP_SUPPORT: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_SKIP_SUPPORT],
  CLIENT_DOCS_RECEIVED_ASSIGN_INTERN: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_ASSIGN_INTERN],
  MODEL_DRAFTED: STATUS_COLORS[ValuationStatus.MODEL_DRAFTED],
  DRAFT_PREPARED: STATUS_COLORS[ValuationStatus.DRAFT_PREPARED],
  DRAFT_REVIEWED_AND_SENT: STATUS_COLORS[ValuationStatus.DRAFT_REVIEWED_AND_SENT],
  CLIENT_APPROVAL_RECEIVED: STATUS_COLORS[ValuationStatus.CLIENT_APPROVAL_RECEIVED],
  REPORT_IN_PROCESS: STATUS_COLORS[ValuationStatus.REPORT_IN_PROCESS],
  FINAL_REPORT_UPLOADED: STATUS_COLORS[ValuationStatus.FINAL_REPORT_UPLOADED],
  FINAL_SENT: STATUS_COLORS[ValuationStatus.FINAL_SENT],
  WAITING: STATUS_COLORS[ValuationStatus.WAITING],
  AWAITING_FINAL_PAYMENT: STATUS_COLORS[ValuationStatus.AWAITING_FINAL_PAYMENT],
  CANCELLED: STATUS_COLORS[ValuationStatus.CANCELLED],
} as const;

export const JOBS_COLOR_PALETTE_KEY = 'jobsColorPalette';
export const JOBS_COLOR_PALETTE_PRESETS_KEY = 'jobsColorPalettePresets';

export type JobsColorPalette = {
  READY_FOR_ONBOARDING: string;
  ONBOARDING_SENT: string;
  ONBOARDING_FINISHED: string;
  CLIENT_DOCS_RECEIVED: string;
  CLIENT_DOCS_RECEIVED_SKIP_SUPPORT: string;
  CLIENT_DOCS_RECEIVED_ASSIGN_INTERN: string;
  MODEL_DRAFTED: string;
  DRAFT_PREPARED: string;
  DRAFT_REVIEWED_AND_SENT: string;
  CLIENT_APPROVAL_RECEIVED: string;
  REPORT_IN_PROCESS: string;
  FINAL_REPORT_UPLOADED: string;
  FINAL_SENT: string;
  WAITING: string;
  AWAITING_FINAL_PAYMENT: string;
  CANCELLED: string;
};

export const JOBS_STATUS_LABELS: Record<keyof JobsColorPalette, string> = {
  READY_FOR_ONBOARDING: 'Ready for Onboarding',
  ONBOARDING_SENT: 'Onboarding Sent',
  ONBOARDING_FINISHED: 'Onboarding Finished',
  CLIENT_DOCS_RECEIVED: 'Client Docs Received',
  CLIENT_DOCS_RECEIVED_SKIP_SUPPORT: 'Client Docs Received (Skip Support)',
  CLIENT_DOCS_RECEIVED_ASSIGN_INTERN: 'Client Docs Received (Assign Intern)',
  MODEL_DRAFTED: 'Model Drafted',
  DRAFT_PREPARED: 'Draft Prepared',
  DRAFT_REVIEWED_AND_SENT: 'Draft Reviewed and Sent',
  CLIENT_APPROVAL_RECEIVED: 'Client Approval Received',
  REPORT_IN_PROCESS: 'Report in Process',
  FINAL_REPORT_UPLOADED: 'Final Report Uploaded',
  FINAL_SENT: 'Final Sent',
  WAITING: 'Waiting',
  AWAITING_FINAL_PAYMENT: 'Awaiting Final Payment',
  CANCELLED: 'Cancelled',
} as const;
// Jobs event colors by status
export const JOBS_EVENT_COLORS = {
  READY_FOR_ONBOARDING: STATUS_COLORS[ValuationStatus.READY_FOR_ONBOARDING],
  ONBOARDING_SENT: STATUS_COLORS[ValuationStatus.ONBOARDING_SENT],
  ONBOARDING_FINISHED: STATUS_COLORS[ValuationStatus.ONBOARDING_FINISHED],
  CLIENT_DOCS_RECEIVED: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED],
  CLIENT_DOCS_RECEIVED_SKIP_SUPPORT: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_SKIP_SUPPORT],
  CLIENT_DOCS_RECEIVED_ASSIGN_INTERN: STATUS_COLORS[ValuationStatus.CLIENT_DOCS_RECEIVED_ASSIGN_INTERN],
  MODEL_DRAFTED: STATUS_COLORS[ValuationStatus.MODEL_DRAFTED],
  DRAFT_PREPARED: STATUS_COLORS[ValuationStatus.DRAFT_PREPARED],
  DRAFT_REVIEWED_AND_SENT: STATUS_COLORS[ValuationStatus.DRAFT_REVIEWED_AND_SENT],
  CLIENT_APPROVAL_RECEIVED: STATUS_COLORS[ValuationStatus.CLIENT_APPROVAL_RECEIVED],
  REPORT_IN_PROCESS: STATUS_COLORS[ValuationStatus.REPORT_IN_PROCESS],
  FINAL_REPORT_UPLOADED: STATUS_COLORS[ValuationStatus.FINAL_REPORT_UPLOADED],
  FINAL_SENT: STATUS_COLORS[ValuationStatus.FINAL_SENT],
  WAITING: STATUS_COLORS[ValuationStatus.WAITING],
  AWAITING_FINAL_PAYMENT: STATUS_COLORS[ValuationStatus.AWAITING_FINAL_PAYMENT],
  CANCELLED: STATUS_COLORS[ValuationStatus.CANCELLED],
  DEFAULT: '#3174ad',
} as const;


// Engagement type constants
export const ENGAGEMENT_TYPE_409A = '409A';
export const ENGAGEMENT_TYPE_504 = '504';
export const ENGAGEMENT_TYPE_ANNUAL_UPDATE = 'Annual Update';
export const ENGAGEMENT_TYPE_ASC_718 = 'ASC 718';
export const ENGAGEMENT_TYPE_ASC_842_LEASE_VALUATION = 'ASC 842 (Lease Valuation)';
export const ENGAGEMENT_TYPE_BOARD_ADVISORY = 'Board Advisory';
export const ENGAGEMENT_TYPE_COMMERCIALIZATION_MODEL = 'Commercialization Model';
export const ENGAGEMENT_TYPE_COMMON_STOCK_CANADA = 'Common Stock - Canada';
export const ENGAGEMENT_TYPE_COMMON_STOCK_OTHER_TAX_FINANCIAL_REPORTING = 'Common Stock - Other Tax/Financial Reporting Purposes';
export const ENGAGEMENT_TYPE_CONTINGENT_CONSIDERATION_ASC_450 = 'Contingent Consideration (ASC 450)';
export const ENGAGEMENT_TYPE_CONVERTIBLE_NOTE_CONVERSION_FEATURE = 'Convertible Note - Conversion Feature';
export const ENGAGEMENT_TYPE_CONVERTIBLE_NOTE_ENTIRE_NOTE = 'Convertible Note - Entire Note';
export const ENGAGEMENT_TYPE_DEBT_VALUATION = 'Debt Valuation';
export const ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_CAPITAL_RAISE = 'Enterprise/Equity Valuation - Capital Raise';
export const ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_SHAREHOLDER_BUYOUT = 'Enterprise/Equity Valuation - Shareholder Buyout';
export const ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_TRANSACTION_NEGOTIATION = 'Enterprise/Equity Valuation - Transaction Negotiation';
export const ENGAGEMENT_TYPE_ENTITY_CONVERSION = 'Entity Conversion';
export const ENGAGEMENT_TYPE_EQUITY_PE = 'Equity-PE';
export const ENGAGEMENT_TYPE_EQUITY_VC = 'Equity-VC';
export const ENGAGEMENT_TYPE_FAIRNESS_OPINION = 'Fairness Opinion';
export const ENGAGEMENT_TYPE_FIXED_ASSET_APPRAISAL = 'Fixed Asset Appraisal';
export const ENGAGEMENT_TYPE_FRESH_START_ACCOUNTING_ASC_852 = 'Fresh Start Accounting (ASC 852)';
export const ENGAGEMENT_TYPE_GENERIC = 'Generic';
export const ENGAGEMENT_TYPE_GOODWILL_IMPAIRMENT_ASC_350 = 'Goodwill Impairment (ASC 350)';
export const ENGAGEMENT_TYPE_IMPAIRMENT_IAS_36 = 'Impairment (IAS 36)';
export const ENGAGEMENT_TYPE_INCREMENTAL_BORROWING_RATE = 'Incremental Borrowing Rate';
export const ENGAGEMENT_TYPE_INITIAL_VALUATION = 'Initial Valuation';
export const ENGAGEMENT_TYPE_INTANGIBLE_ASSET_IMPAIRMENT_ASC_360 = 'Intangible Asset Impairment (ASC 360)';
export const ENGAGEMENT_TYPE_INTELLECTUAL_PROPERTY = 'Intellectual Property';
export const ENGAGEMENT_TYPE_LETTER_OF_OPINION = 'Letter of Opinion';
export const ENGAGEMENT_TYPE_MARITAL_DISSOLUTION = 'Marital Dissolution';
export const ENGAGEMENT_TYPE_NONCASH_CHARITABLE_CONTRIBUTION_FORM_8283 = 'Noncash Charitable Contribution (Form 8283)';
export const ENGAGEMENT_TYPE_OTHER = 'Other';
export const ENGAGEMENT_TYPE_OTHER_LITIGATION = 'Other Litigation';
export const ENGAGEMENT_TYPE_PERFORMANCE_BASED_OPTIONS_ASC_718 = 'Performance Based Options (ASC 718)';
export const ENGAGEMENT_TYPE_PHANTOM_STOCK = 'Phantom Stock';
export const ENGAGEMENT_TYPE_PPA_ASC = 'PPA (ASC)';
export const ENGAGEMENT_TYPE_PPA_IFRS_3 = 'PPA (IFRS 3)';
export const ENGAGEMENT_TYPE_PROFITS_INTERESTS_EXPENSING_PREVIOUS_GRANTS = 'Profits Interests - Expensing Previous Grants';
export const ENGAGEMENT_TYPE_PROFITS_INTERESTS_SETTING_THRESHOLD = 'Profits Interests - Setting Threshold';
export const ENGAGEMENT_TYPE_SBA_7A = 'SBA 7(a)';
export const ENGAGEMENT_TYPE_SHAREHOLDER_DISPUTES = 'Shareholder Disputes';
export const ENGAGEMENT_TYPE_SOLVENCY_OPINION = 'Solvency Opinion';
export const ENGAGEMENT_TYPE_SUBSCRIPTION = 'Subscription';
export const ENGAGEMENT_TYPE_TAX_VALUATION_GENERIC = 'Tax Valuation (Generic)';
export const ENGAGEMENT_TYPE_TOKEN_VALUATION = 'Token Valuation';
export const ENGAGEMENT_TYPE_TRANSFER = 'Transfer';
export const ENGAGEMENT_TYPE_UNDERLYING_SECURITIES = 'Underlying Securities';
export const ENGAGEMENT_TYPE_WARRANT = 'Warrant';

// Engagement type color constants
export const COLOR_CORAL = '#f5a585';
export const COLOR_LIGHT_GREEN = '#a8dda8';
export const COLOR_TEAL = '#7ac8c3';
export const COLOR_PINK = '#f19c99';
export const COLOR_PURPLE = '#b197e4';
export const COLOR_LIGHT_GRAY = '#e9e9e9';
export const COLOR_YELLOW = '#fbe798';

// Engagement type to color map
export const ENGAGEMENT_TYPE_COLOR_MAP = {
  [ENGAGEMENT_TYPE_409A]: COLOR_CORAL,
  [ENGAGEMENT_TYPE_504]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_ANNUAL_UPDATE]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_ASC_718]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_ASC_842_LEASE_VALUATION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_BOARD_ADVISORY]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_COMMERCIALIZATION_MODEL]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_COMMON_STOCK_CANADA]: COLOR_CORAL,
  [ENGAGEMENT_TYPE_COMMON_STOCK_OTHER_TAX_FINANCIAL_REPORTING]: COLOR_TEAL,
  [ENGAGEMENT_TYPE_CONTINGENT_CONSIDERATION_ASC_450]: COLOR_TEAL,
  [ENGAGEMENT_TYPE_CONVERTIBLE_NOTE_CONVERSION_FEATURE]: COLOR_PINK,
  [ENGAGEMENT_TYPE_CONVERTIBLE_NOTE_ENTIRE_NOTE]: COLOR_PURPLE,
  [ENGAGEMENT_TYPE_DEBT_VALUATION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_CAPITAL_RAISE]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_SHAREHOLDER_BUYOUT]: COLOR_LIGHT_GRAY,
  [ENGAGEMENT_TYPE_ENTERPRISE_EQUITY_VALUATION_TRANSACTION_NEGOTIATION]: COLOR_YELLOW,
  [ENGAGEMENT_TYPE_ENTITY_CONVERSION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_EQUITY_PE]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_EQUITY_VC]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_FAIRNESS_OPINION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_FIXED_ASSET_APPRAISAL]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_FRESH_START_ACCOUNTING_ASC_852]: COLOR_PINK,
  [ENGAGEMENT_TYPE_GENERIC]: COLOR_LIGHT_GRAY,
  [ENGAGEMENT_TYPE_GOODWILL_IMPAIRMENT_ASC_350]: COLOR_PINK,
  [ENGAGEMENT_TYPE_IMPAIRMENT_IAS_36]: COLOR_LIGHT_GRAY,
  [ENGAGEMENT_TYPE_INCREMENTAL_BORROWING_RATE]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_INITIAL_VALUATION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_INTANGIBLE_ASSET_IMPAIRMENT_ASC_360]: COLOR_PURPLE,
  [ENGAGEMENT_TYPE_INTELLECTUAL_PROPERTY]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_LETTER_OF_OPINION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_MARITAL_DISSOLUTION]: COLOR_YELLOW,
  [ENGAGEMENT_TYPE_NONCASH_CHARITABLE_CONTRIBUTION_FORM_8283]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_OTHER]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_OTHER_LITIGATION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_PERFORMANCE_BASED_OPTIONS_ASC_718]: COLOR_TEAL,
  [ENGAGEMENT_TYPE_PHANTOM_STOCK]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_PPA_ASC]: COLOR_TEAL,
  [ENGAGEMENT_TYPE_PPA_IFRS_3]: COLOR_CORAL,
  [ENGAGEMENT_TYPE_PROFITS_INTERESTS_EXPENSING_PREVIOUS_GRANTS]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_PROFITS_INTERESTS_SETTING_THRESHOLD]: COLOR_PURPLE,
  [ENGAGEMENT_TYPE_SBA_7A]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_SHAREHOLDER_DISPUTES]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_SOLVENCY_OPINION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_SUBSCRIPTION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_TAX_VALUATION_GENERIC]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_TOKEN_VALUATION]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_TRANSFER]: COLOR_LIGHT_GREEN,
  [ENGAGEMENT_TYPE_UNDERLYING_SECURITIES]: COLOR_TEAL,
  [ENGAGEMENT_TYPE_WARRANT]: COLOR_LIGHT_GREEN,
} as const;

export const MODELS_VIEWS_KEY = 'saved-model-views';
export const ACTIVE_MODEL_VIEW_KEY = 'active-model-view';
export const MODELS_SORT_KEY = 'modelsSort';
export const MODELS_VIEW_KEY = 'modelsWorkboardView';
export const DEFAULT_VIEW_ID = 'default';

export const MODELS_COLUMN_IDS = {
  COMPANY_NAME: 'company_name',
  VALUATION_STATUS: 'valuation_status',
  MODEL_STATUS: 'model_status',
  SUPPORTING_DUE_DATE: 'model_due_date',
  NOTES: 'notes',
  MODEL_REVIEWER: 'model_reviewer',
  MODEL_PREPARER: 'model_preparer',
  VALUATION_LEAD: 'valuation_lead',
  VALUATION_DATE: 'valuation_date',
} as const;

export const MODELS_COLUMN_LABELS = {
  COMPANY_NAME: 'Company Name',
  VALUATION_STATUS: 'Valuation Status',
  MODEL_STATUS: 'Model Status',
  SUPPORTING_DUE_DATE: 'Supporting Due Date',
  NOTES: 'Notes',
  MODEL_REVIEWER: 'Reviewer',
  MODEL_PREPARER: 'Preparer',
  VALUATION_LEAD: 'Valuation Lead',
  VALUATION_DATE: 'Valuation Date',
} as const;

export {
  DRAFT,
  REVIEW,
  IN_PROCESS_SCALAR_USA,
  FINAL,
  REVIEW_FINAL,
  COMPLETE,
  READY_FOR_ONBOARDING,
  ONBOARDING_SENT,
  ONBOARDING_FINISHED,
  CLIENT_DOCS_RECEIVED,
  CLIENT_DOCS_RECEIVED_SKIP_SUPPORT,
  CLIENT_DOCS_RECEIVED_ASSIGN_INTERN,
  MODEL_DRAFTED,
  DRAFT_PREPARED,
  DRAFT_REVIEWED_AND_SENT,
  CLIENT_APPROVAL_RECEIVED,
  REPORT_IN_PROCESS,
  FINAL_REPORT_UPLOADED,
  FINAL_SENT,
  WAITING,
  AWAITING_FINAL_PAYMENT,
  CANCELLED,
};
